/**
 * Tokens de cor dos gráficos do painel de squads.
 *
 * As séries NÃO são escolhidas a olho: foram validadas contra as superfícies reais
 * do app (#FFFFFF no claro, #2D2D2D no escuro) para banda de luminosidade, piso de
 * croma, separação sob daltonismo (protanopia/deuteranopia) e contraste.
 *
 * Duas ressalvas que a validação impõe e o painel cumpre:
 *  - amarelo e magenta ficam abaixo de 3:1 no claro, e o verde no escuro → todo
 *    gráfico traz rótulo direto e tabela equivalente, nunca só a cor;
 *  - no escuro, verde × amarelo ficam na faixa de piso sob daltonismo (ΔE 6.9) →
 *    o rótulo direto é o que separa os dois, não a cor.
 *
 * O azul da marca (#00377B) é escuro demais para servir de marca de dado: usamos um
 * passo mais claro da mesma família. Ele segue valendo para chrome (cabeçalho, botão).
 */

export type Modo = 'light' | 'dark';

/** Ordem fixa. A cor acompanha o squad, nunca a posição no ranking. */
const SERIES_CLARO = ['#1f5fa9', '#eda100', '#008300', '#e87ba4'];
const SERIES_ESCURO = ['#3987e5', '#c98500', '#008300', '#d55181'];

export interface VizTheme {
  serie: (indice: number) => string;
  series: string[];
  superficie: string;
  grade: string;
  eixo: string;
  textoPrimario: string;
  textoSecundario: string;
  textoSuave: string;
  /** Cinza de contexto: usado quando um item é o assunto e o resto é pano de fundo. */
  neutro: string;
  /** Par divergente (frio ↔ quente) com cinza neutro no meio. */
  divergentePositivo: string;
  divergenteNegativo: string;
  divergenteNeutro: string;
  tooltipFundo: string;
  tooltipBorda: string;
}

const CLARO: VizTheme = {
  series: SERIES_CLARO,
  serie: (i) => SERIES_CLARO[i % SERIES_CLARO.length],
  superficie: '#FFFFFF',
  grade: '#E5E5E5',
  eixo: '#C2D0D7',
  textoPrimario: '#4D4D4D',
  textoSecundario: '#6B6B6B',
  textoSuave: '#A7A7A7',
  neutro: '#C2D0D7',
  divergentePositivo: '#2a78d6',
  divergenteNegativo: '#e34948',
  divergenteNeutro: '#E5E5E5',
  tooltipFundo: '#FFFFFF',
  tooltipBorda: '#C2D0D7',
};

const ESCURO: VizTheme = {
  series: SERIES_ESCURO,
  serie: (i) => SERIES_ESCURO[i % SERIES_ESCURO.length],
  superficie: '#2D2D2D',
  grade: '#4D4D4D',
  eixo: '#4D4D4D',
  textoPrimario: '#FFFFFF',
  textoSecundario: '#C2D0D7',
  textoSuave: '#A7A7A7',
  neutro: '#4D4D4D',
  divergentePositivo: '#3987e5',
  divergenteNegativo: '#e66767',
  divergenteNeutro: '#4D4D4D',
  tooltipFundo: '#2D2D2D',
  tooltipBorda: '#4D4D4D',
};

export const obterVizTheme = (modo: Modo): VizTheme => (modo === 'dark' ? ESCURO : CLARO);
